import { useRef, useState, type ChangeEvent } from 'react'
import { uploadStageFile, type Uploader } from '@/data/fileApi'
import { errorMessage } from '@/lib/errors'
import { extractProofMetadata } from '@/lib/exif'
import { getDeviceLocation } from '@/lib/geolocation'
import { CAMERA_ACCEPT, UPLOAD_ACCEPT, validateUploadFile } from '@/lib/upload'
import type { ProofMetadata } from '@/types/file'

interface FileUploadProps {
  projectId: string
  stage: number
  uploader: Uploader
  /** 'camera' forces the device camera + geolocation (Proof-of-Product media). */
  variant?: 'file' | 'camera'
  /** Override the button text (e.g. "Upload document"). */
  label?: string
}

export function FileUpload({
  projectId,
  stage,
  uploader,
  variant = 'file',
  label,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isCamera = variant === 'camera'
  const buttonLabel = label ?? (isCamera ? 'Take photo / video' : 'Upload file')
  const buttonTestId = isCamera ? `stage-camera-${stage}` : `stage-upload-${stage}`
  const inputTestId = isCamera ? `stage-camera-input-${stage}` : `stage-file-input-${stage}`

  async function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) {
      return
    }
    const validationError = validateUploadFile(file, { allowVideo: isCamera })
    if (validationError) {
      setError(validationError)
      return
    }
    setError(null)
    setUploading(true)
    try {
      let metadata: ProofMetadata | undefined
      if (isCamera) {
        // Force the location: capture the device's live GPS at upload time (web
        // camera photos have no EXIF GPS). Fall back to any EXIF the file carries.
        const [device, exif] = await Promise.all([
          getDeviceLocation(),
          file.type.startsWith('image/')
            ? extractProofMetadata(file)
            : Promise.resolve<ProofMetadata>({ gps: null, takenAt: null, camera: null }),
        ])
        metadata = {
          gps: device ?? exif.gps,
          takenAt: exif.takenAt,
          camera: exif.camera,
          locationSource: device ? 'device' : exif.gps ? 'exif' : null,
        }
      }
      await uploadStageFile(projectId, stage, file, uploader, metadata)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={isCamera ? CAMERA_ACCEPT : UPLOAD_ACCEPT}
        // Camera variant opens the camera directly on mobile (no gallery/browse).
        // Ignored on desktop (falls back to a file picker).
        capture={isCamera ? 'environment' : undefined}
        onChange={handleChange}
        className="hidden"
        data-testid={inputTestId}
        aria-label={`${buttonLabel} for stage ${stage}`}
      />
      <button
        type="button"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        data-testid={buttonTestId}
        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
      >
        {uploading ? 'Uploading…' : buttonLabel}
      </button>
      {error && (
        <p role="alert" className="mt-1 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  )
}
